import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, MenuItem, TextField } from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { Plus, TicketPercent } from 'lucide-react';
import { CampaignFormDialog } from '../components/CampaignFormDialog';
import { CampaignStatusChip } from '../components/CampaignStatusChip';
import { useCampaignList, useCreateCampaign } from '../hooks/useCampaigns';
import { discountLabel, errorText, formatDateTime } from '../format';
import { useNotification } from '../../../../hooks/useNotification';
import type { Campaign, CampaignStatus } from '../types';

export const CampaignListPage: React.FC = () => {
  const navigate = useNavigate();
  const notification = useNotification();
  const [status, setStatus] = useState<CampaignStatus | ''>('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { data, isLoading } = useCampaignList({
    status,
    search: search.trim() || undefined,
    page: pagination.page + 1,
    pageSize: pagination.pageSize,
  });
  const create = useCreateCampaign();

  const columns: GridColDef<Campaign>[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 220 },
    {
      field: 'status',
      headerName: 'Status',
      width: 170,
      renderCell: (p) => (
        <CampaignStatusChip status={p.row.status} ended={new Date(p.row.validTo) < new Date()} />
      ),
    },
    {
      field: 'discount',
      headerName: 'Discount',
      width: 190,
      sortable: false,
      valueGetter: (_v, row) => discountLabel(row),
    },
    { field: 'appliesTo', headerName: 'Applies to', width: 130 },
    {
      field: 'validity',
      headerName: 'Valid',
      width: 340,
      sortable: false,
      valueGetter: (_v, row) => `${formatDateTime(row.validFrom)} → ${formatDateTime(row.validTo)}`,
    },
    {
      field: 'used',
      headerName: 'Used / max',
      width: 110,
      sortable: false,
      valueGetter: (_v, row) => `${row.usedCount} / ${row.maxRedemptions ?? '∞'}`,
    },
    {
      field: 'coupons',
      headerName: 'Coupons',
      width: 100,
      sortable: false,
      valueGetter: (_v, row) => row._count?.coupons ?? 0,
    },
  ];

  const submit = (input: Parameters<typeof create.mutate>[0]) => {
    setFormError(null);
    create.mutate(input, {
      onSuccess: (campaign) => {
        notification.success('Campaign created');
        setFormOpen(false);
        navigate(`/campaigns/${campaign.publicId}`);
      },
      onError: (err) => setFormError(errorText(err)),
    });
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TicketPercent size={22} />
          <h1 className="text-xl font-semibold">Campaigns</h1>
        </div>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => {
            setFormError(null);
            setFormOpen(true);
          }}
        >
          New campaign
        </Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <TextField
          size="small"
          label="Search name"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          sx={{ minWidth: 150 }}
          onChange={(e) => {
            setStatus(e.target.value as CampaignStatus | '');
            setPagination((p) => ({ ...p, page: 0 }));
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="DRAFT">Draft</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="PAUSED">Paused</MenuItem>
        </TextField>
      </div>
      <DataGrid
        autoHeight
        rows={data?.items ?? []}
        getRowId={(row) => row.publicId}
        columns={columns}
        loading={isLoading}
        rowCount={data?.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(`/campaigns/${params.row.publicId}`)}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        localeText={{
          noRowsLabel: 'No campaigns yet. Create one to start issuing discount codes.',
        }}
      />
      <CampaignFormDialog
        open={formOpen}
        saving={create.isPending}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={submit}
      />
    </div>
  );
};
